/**
 * AddAppointmentModal.js
 * Modal para crear una nueva cita desde la agenda del veterinario
 */
import React, { useState, useEffect } from "react";
import { crearCitaDeVet, getPacientesDeVet } from "../../services/api";

const MOTIVOS = [
  { value: "consulta", label: "Consulta general" },
  { value: "vacunacion", label: "Vacunacion" },
  { value: "cirugia", label: "Cirugia" },
  { value: "seguimiento", label: "Seguimiento" },
  { value: "emergencia", label: "Emergencia" },
  { value: "otro", label: "Otro" },
];

// Hours for 12-hour format (1-12)
const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);

// Minutes in 15-minute intervals
const MINUTES_15 = ["00", "15", "30", "45"];

// LemmonTags theme colors (light)
const COLORS = {
  bgMain: '#ffffff',
  bgCard: '#ffffff',
  bgSecondary: '#f8f9fa',
  textPrimary: '#374151',
  textSecondary: '#6b7280',
  textMuted: '#9ca3af',
  accent: '#84cc16',
  accentHover: '#65a30d',
  border: '#e5e7eb',
  borderLight: '#d1d5db',
  danger: '#ef4444',
};

// Convert 12-hour to 24-hour format
function to24Hour(hour, minute, period) {
  let h = parseInt(hour, 10);
  if (period === "PM" && h !== 12) h += 12;
  if (period === "AM" && h === 12) h = 0;
  return `${h.toString().padStart(2, "0")}:${minute}`;
}

export default function AddAppointmentModal({ vetId, vetNombre, onClose, onSuccess }) {
  const [pacientes, setPacientes] = useState([]);
  const [loadingPacientes, setLoadingPacientes] = useState(true);

  const [formData, setFormData] = useState({
    pacienteId: "",
    fecha: "",
    hora: "9",
    minuto: "00",
    periodo: "AM",
    motivo: "consulta",
    notas: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    async function loadPacientes() {
      try {
        const resp = await getPacientesDeVet(vetId);
        if (resp.data) {
          setPacientes(resp.data);
        }
      } catch (err) {
        console.error("Error loading patients:", err);
      }
      setLoadingPacientes(false);
    }
    loadPacientes();
  }, [vetId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.pacienteId) {
      setError("Selecciona un paciente");
      return;
    }
    if (!formData.fecha) {
      setError("Selecciona una fecha");
      return;
    }
    setLoading(true);

    try {
      const paciente = pacientes.find((p) => p.id === formData.pacienteId);
      const horaInicio = to24Hour(formData.hora, formData.minuto, formData.periodo);

      // Para pacientes LemmonTag, incluir ownerId y objetoId para que
      // el dueño pueda ver la cita en su panel "Gestionar Citas"
      const esLemmonTag = paciente?.tipo === "lemmontag";
      const ownerId = paciente?.ownerId || paciente?.userId;

      const citaData = {
        fecha: formData.fecha,
        horaInicio,
        motivo: formData.motivo,
        notas: formData.notas,
        estado: "confirmada",
        objetoId: formData.pacienteId,
        objetoName: paciente?.nombre || "Paciente",
        creadaPorVet: true,
        vetNombre: vetNombre || "Veterinario",
        // Campos necesarios para que el dueño vea la cita
        ...(esLemmonTag && ownerId && {
          ownerId: ownerId,
          objetoId: paciente.id,
        }),
      };

      await crearCitaDeVet(vetId, citaData);
      setLoading(false);
      onSuccess();
    } catch (err) {
      setError(err.message || "Error al crear la cita");
      setLoading(false);
    }
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split("T")[0];

  const inputStyle = {
    backgroundColor: COLORS.bgSecondary,
    color: COLORS.textPrimary,
    border: `1px solid ${COLORS.border}`
  };

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100"
      style={{
        backgroundColor: "rgba(0,0,0,0.7)",
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "20px",
      }}
    >
      <div
        className="card shadow border-0"
        style={{
          width: "100%",
          maxWidth: "500px",
          maxHeight: "calc(100vh - 40px)",
          display: "flex",
          flexDirection: "column",
          backgroundColor: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`,
        }}
      >
        <div
          className="card-header d-flex justify-content-between align-items-center"
          style={{
            flexShrink: 0,
            backgroundColor: COLORS.bgSecondary,
            borderBottom: `1px solid ${COLORS.border}`
          }}
        >
          <h5 className="mb-0" style={{ color: COLORS.textPrimary }}>
            <i className="bi bi-calendar-plus me-2" style={{ color: COLORS.accent }}></i>
            Nueva Cita
          </h5>
          <button
            type="button"
            className="btn-close"
            onClick={onClose}
            disabled={loading}
          />
        </div>

        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", overflow: "hidden", flex: 1 }}>
          <div className="card-body" style={{ overflowY: "auto", flex: 1 }}>
            {error && (
              <div className="alert py-2" style={{ backgroundColor: 'rgba(239, 68, 68, 0.2)', color: COLORS.danger, border: `1px solid ${COLORS.danger}` }}>{error}</div>
            )}

            {/* Paciente */}
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.textPrimary }}>
                Paciente <span style={{ color: COLORS.danger }}>*</span>
              </label>
              {loadingPacientes ? (
                <p style={{ color: COLORS.textSecondary }}>Cargando pacientes...</p>
              ) : pacientes.length === 0 ? (
                <p style={{ color: COLORS.textSecondary }}>No tienes pacientes registrados</p>
              ) : (
                <select
                  className="form-select"
                  name="pacienteId"
                  value={formData.pacienteId}
                  onChange={handleChange}
                  required
                  style={inputStyle}
                >
                  <option value="">Seleccionar paciente...</option>
                  {pacientes.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nombre} {p.tipo === "externo" ? "(Externo)" : ""} - {p.nombreDueno || p.ownerName || ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Fecha */}
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.textPrimary }}>
                Fecha <span style={{ color: COLORS.danger }}>*</span>
              </label>
              <input
                type="date"
                className="form-control"
                name="fecha"
                value={formData.fecha}
                onChange={handleChange}
                min={today}
                required
                style={inputStyle}
              />
            </div>

            {/* Hora - 3 selectores separados */}
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.textPrimary }}>
                Hora <span style={{ color: COLORS.danger }}>*</span>
              </label>
              <div className="d-flex gap-2 align-items-center">
                <select
                  className="form-select"
                  name="hora"
                  value={formData.hora}
                  onChange={handleChange}
                  style={{ ...inputStyle, width: "80px" }}
                >
                  {HOURS_12.map((h) => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
                <span style={{ color: COLORS.textPrimary, fontWeight: "bold" }}>:</span>
                <select
                  className="form-select"
                  name="minuto"
                  value={formData.minuto}
                  onChange={handleChange}
                  style={{ ...inputStyle, width: "80px" }}
                >
                  {MINUTES_15.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
                <select
                  className="form-select"
                  name="periodo"
                  value={formData.periodo}
                  onChange={handleChange}
                  style={{ ...inputStyle, width: "80px" }}
                >
                  <option value="AM">AM</option>
                  <option value="PM">PM</option>
                </select>
              </div>
            </div>

            {/* Motivo */}
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.textPrimary }}>
                Motivo <span style={{ color: COLORS.danger }}>*</span>
              </label>
              <select
                className="form-select"
                name="motivo"
                value={formData.motivo}
                onChange={handleChange}
                required
                style={inputStyle}
              >
                {MOTIVOS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Notas */}
            <div className="mb-3">
              <label className="form-label" style={{ color: COLORS.textPrimary }}>Notas</label>
              <textarea
                className="form-control"
                name="notas"
                value={formData.notas}
                onChange={handleChange}
                rows={2}
                placeholder="Notas adicionales..."
                style={inputStyle}
              />
            </div>
          </div>

          <div
            className="card-footer d-flex justify-content-end gap-2"
            style={{
              flexShrink: 0,
              backgroundColor: COLORS.bgSecondary,
              borderTop: `1px solid ${COLORS.border}`
            }}
          >
            <button
              type="button"
              className="btn"
              style={{ backgroundColor: 'transparent', color: COLORS.textPrimary, border: `1px solid ${COLORS.borderLight}` }}
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn"
              style={{ backgroundColor: COLORS.accent, color: "#fff", border: "none" }}
              disabled={loading || loadingPacientes || pacientes.length === 0}
            >
              {loading ? "Guardando..." : "Crear Cita"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
